/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-05-25 16:08:40
 */
import React, { useCallback, useMemo } from 'react'
import { Dropdown, Button } from 'antd'
import type { MenuProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  CaretDownOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'

type MenuItem = Required<MenuProps>['items'][number]

interface RangeYearMonthPickerProps {
  pickerValue: [Dayjs, Dayjs]
  onPickerValueChange: (dates: [Dayjs, Dayjs]) => void
}

const createYearsMenuItems = (): MenuItem[] => {
  const years = dayjs().diff(dayjs('2016-01-01'), 'years')
  const menuItems: MenuItem[] = []
  for (let index = 0; index <= years; index++) {
    menuItems.push({
      key: String(2016 + index),
      label: String(2016 + index)
    })
  }
  return menuItems
}

const createMonthsMenuItems = (): MenuItem[] => {
  // 0 到 11
  const menuItems: MenuItem[] = []
  for (let index = 0; index <= 11; index++) {
    menuItems.push({
      key: String(index),
      label: index > 8 ? String(index + 1) : '0' + (index + 1)
    })
  }
  return menuItems
}

const RangeYearMonthPicker: React.FC<RangeYearMonthPickerProps> = (props) => {
  const { pickerValue, onPickerValueChange } = props
  const leftYear = useMemo(() => pickerValue[0].year(), [pickerValue])
  const leftMonth = useMemo(() => pickerValue[0].month(), [pickerValue])
  const rightYear = useMemo(() => pickerValue[1].year(), [pickerValue])
  const rightMonth = useMemo(() => pickerValue[1].month(), [pickerValue])

  const leftYearsMenuProps: MenuProps = useMemo(
    () => ({
      items: createYearsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(leftYear)],
      onClick: ({ key }) =>
        onPickerValueChange([
          dayjs(pickerValue[0]).year(Number(key)),
          pickerValue[1]
        ])
    }),
    [leftYear, pickerValue, onPickerValueChange]
  )

  const leftMonthsMenuProps: MenuProps = useMemo(
    () => ({
      items: createMonthsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(leftMonth)],
      onClick: ({ key }) =>
        onPickerValueChange([
          dayjs(pickerValue[0]).month(Number(key)),
          pickerValue[1]
        ])
    }),
    [leftMonth, pickerValue, onPickerValueChange]
  )

  const rightYearsMenuProps: MenuProps = useMemo(
    () => ({
      items: createYearsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(rightYear)],
      onClick: ({ key }) =>
        onPickerValueChange([
          pickerValue[0],
          dayjs(pickerValue[1]).year(Number(key))
        ])
    }),
    [rightYear, pickerValue, onPickerValueChange]
  )

  const rightMonthsMenuProps: MenuProps = useMemo(
    () => ({
      items: createMonthsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(rightMonth)],
      onClick: ({ key }) =>
        onPickerValueChange([
          pickerValue[0],
          dayjs(pickerValue[1]).month(Number(key))
        ])
    }),
    [rightMonth, pickerValue, onPickerValueChange]
  )

  const onPrevMonth = useCallback(() => {
    onPickerValueChange([
      dayjs(pickerValue[0]).add(-1, 'months'),
      pickerValue[1]
    ])
  }, [pickerValue, onPickerValueChange])

  const onNextMonth = useCallback(() => {
    onPickerValueChange([
      pickerValue[0],
      dayjs(pickerValue[1]).add(1, 'months')
    ])
  }, [pickerValue, onPickerValueChange])

  return (
    <div className="range-year-month-picker">
      <Button
        type="link"
        size="small"
        style={{ marginRight: '8px', color: '#222222' }}
        onClick={onPrevMonth}
      >
        <LeftOutlined />
      </Button>
      <Dropdown menu={leftYearsMenuProps}>
        <Button className="btn-year" size="small">
          {leftYear}年<CaretDownOutlined />
        </Button>
      </Dropdown>
      <Dropdown menu={leftMonthsMenuProps}>
        <Button className="btn-month" size="small">
          {leftMonth > 8 ? leftMonth + 1 : '0' + (leftMonth + 1)}月
          <CaretDownOutlined />
        </Button>
      </Dropdown>
      <div className="space"></div>
      <Dropdown menu={rightYearsMenuProps}>
        <Button className="btn-year" size="small">
          {rightYear}年<CaretDownOutlined />
        </Button>
      </Dropdown>
      <Dropdown menu={rightMonthsMenuProps}>
        <Button className="btn-month" size="small">
          {rightMonth > 8 ? rightMonth + 1 : '0' + (rightMonth + 1)}月
          <CaretDownOutlined />
        </Button>
      </Dropdown>
      <Button
        type="link"
        size="small"
        style={{ marginLeft: '8px', color: '#222222' }}
        onClick={onNextMonth}
      >
        <RightOutlined />
      </Button>
    </div>
  )
}

export default RangeYearMonthPicker
